import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/Dialog";
import { Kbd } from "@/shared/ui/Kbd";
import { Separator } from "@/shared/ui/Separator";
import { useHotkeyStore } from "@/shared/hooks/useHotkeys";

/**
 * HotkeyCheatsheet — opens on ⌘/ (Cmd+/ or Ctrl+/). Lists every
 * registered binding grouped by `group`. Mounted once at the app shell
 * so any feature that registers a hotkey appears here automatically.
 */
export function HotkeyCheatsheet() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const list = useHotkeyStore((s) => s.list);
  const bindings = useHotkeyStore((s) => s.bindings);

  useEffect(() => {
    function onOpen() { setOpen(true); }
    window.addEventListener("expertgas:cheatsheet", onOpen);
    return () => window.removeEventListener("expertgas:cheatsheet", onOpen);
  }, []);

  const grouped = useMemo(() => {
    const items = list();
    const map = new Map<string, typeof items>();
    for (const b of items) {
      const key = b.group ?? t("widgets.hotkeys.otherGroup");
      const arr = map.get(key) ?? [];
      arr.push(b);
      map.set(key, arr);
    }
    return Array.from(map.entries());
    // bindings is a stable Map ref unless something registered/unregistered
  }, [list, bindings, t]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("widgets.hotkeys.title")}</DialogTitle>
          <DialogDescription>
            {t("widgets.hotkeys.description")}
          </DialogDescription>
        </DialogHeader>
        <Separator />
        <DialogBody className="max-h-[60vh] overflow-y-auto space-y-5">
          {grouped.map(([group, items]) => (
            <section key={group}>
              <div className="mb-2 text-[11px] uppercase tracking-[0.07em] font-semibold text-[var(--fg-subtle)]">
                {group}
              </div>
              <ul className="divide-y divide-[var(--separator)]">
                {items.map((b) => (
                  <li
                    key={b.combo}
                    className="flex items-center justify-between gap-4 py-2.5"
                  >
                    <span className="text-[14px] text-[var(--fg)]">{b.label}</span>
                    <Kbd keys={b.combo.split("+").map((p) => p === "mod" ? "mod" : p === "alt" ? "alt" : p === "shift" ? "shift" : p.toUpperCase())} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
